import React, { useState, useMemo } from 'react';
import { Users, UserCheck, UserX, Check, X, ChevronDown, ChevronUp, Shield } from 'lucide-react';

/**
 * ReviewShareModal
 * 
 * Modal overlay shown after photo capture. Allows the photographer
 * to review detected faces (primary vs background) and choose who
 * to share the photo with.
 *
 * Props:
 *  - open: boolean
 *  - photoUrl: string (presigned S3 URL for the captured photo)
 *  - matchedFaces: Array<{ recipient_id, recipient_name, classification, similarity, cached_approval, auto_shared }>
 *  - onConfirm: (selectedRecipientIds: string[]) => void
 *  - onSkip: () => void   (share with all primary faces)
 *  - onCancel: () => void  (keep photo private / owner-only)
 */
function ReviewShareModal({ open, photoUrl, matchedFaces = [], onConfirm, onSkip, onCancel }) {
  // Default: primary faces are selected, background faces are not
  const initialSelected = useMemo(() => {
    const sel = {};
    matchedFaces.forEach((f) => {
      sel[f.recipient_id] = f.classification === 'primary' || f.auto_shared;
    });
    return sel;
  }, [matchedFaces]);

  const [selected, setSelected] = useState(initialSelected);
  const [showBackground, setShowBackground] = useState(false);
  const [confirming, setConfirming] = useState(false);

  if (!open) return null;

  const primaryFaces = matchedFaces.filter((f) => f.classification === 'primary');
  const backgroundFaces = matchedFaces.filter((f) => f.classification === 'background');
  const hasBackground = backgroundFaces.length > 0;

  const selectedCount = Object.values(selected).filter(Boolean).length;
  const totalMatches = matchedFaces.length;

  const toggleFace = (recipientId) => {
    // Don't allow toggling auto-shared faces off
    const face = matchedFaces.find((f) => f.recipient_id === recipientId);
    if (face && face.auto_shared) return;

    setSelected((prev) => ({
      ...prev,
      [recipientId]: !prev[recipientId],
    }));
  };

  const handleConfirm = async () => {
    setConfirming(true);
    const recipientIds = Object.entries(selected)
      .filter(([, v]) => v)
      .map(([k]) => k);
    await onConfirm(recipientIds);
    setConfirming(false);
  };

  const FaceItem = ({ face }) => {
    const isSelected = selected[face.recipient_id];
    const isAutoShared = face.auto_shared;
    const isPrimary = face.classification === 'primary';

    return (
      <button
        onClick={() => toggleFace(face.recipient_id)}
        disabled={isAutoShared}
        data-testid={`face-toggle-${face.recipient_id}`}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '0.75rem',
          width: '100%',
          padding: '0.75rem',
          borderRadius: '0.75rem',
          border: isSelected
            ? '2px solid hsl(188, 82%, 35%)'
            : '2px solid hsl(210, 12%, 92%)',
          background: isSelected
            ? 'hsl(188, 82%, 35%, 0.08)'
            : 'hsl(0, 0%, 100%)',
          cursor: isAutoShared ? 'default' : 'pointer',
          opacity: isAutoShared ? 0.7 : 1,
          transition: 'border-color 200ms ease, background-color 200ms ease',
        }}
      >
        {/* Avatar circle */}
        <div
          style={{
            width: '40px',
            height: '40px',
            borderRadius: '50%',
            background: isPrimary
              ? 'hsl(188, 82%, 35%)'
              : 'hsl(210, 12%, 92%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          {isPrimary ? (
            <UserCheck size={18} color="white" />
          ) : (
            <UserX size={18} color="hsl(215, 16%, 35%)" />
          )}
        </div>

        {/* Name and info */}
        <div style={{ flex: 1, textAlign: 'left' }}>
          <div
            style={{
              fontSize: '0.875rem',
              fontWeight: 600,
              color: 'hsl(224, 71%, 4%)',
              lineHeight: 1.3,
            }}
          >
            {face.recipient_name}
          </div>
          <div
            style={{
              fontSize: '0.7rem',
              color: 'hsl(215, 16%, 35%)',
              marginTop: '0.125rem',
              display: 'flex',
              alignItems: 'center',
              gap: '0.375rem',
            }}
          >
            <span
              style={{
                padding: '0.125rem 0.375rem',
                borderRadius: '9999px',
                fontSize: '0.625rem',
                fontWeight: 600,
                background: isPrimary
                  ? 'hsl(162, 72%, 34%, 0.15)'
                  : 'hsl(28, 92%, 50%, 0.15)',
                color: isPrimary
                  ? 'hsl(162, 72%, 34%)'
                  : 'hsl(28, 92%, 50%)',
              }}
            >
              {isPrimary ? 'Primary' : 'Background'}
            </span>
            {face.cached_approval && (
              <span
                style={{
                  fontSize: '0.625rem',
                  color: 'hsl(162, 72%, 34%)',
                  fontWeight: 500,
                }}
              >
                Previously approved
              </span>
            )}
            {isAutoShared && (
              <span
                style={{
                  fontSize: '0.625rem',
                  color: 'hsl(188, 82%, 35%)',
                  fontWeight: 500,
                }}
              >
                Auto-shared
              </span>
            )}
          </div>
        </div>

        {/* Checkbox indicator */}
        <div
          style={{
            width: '24px',
            height: '24px',
            borderRadius: '6px',
            border: isSelected
              ? '2px solid hsl(188, 82%, 35%)'
              : '2px solid hsl(210, 12%, 82%)',
            background: isSelected ? 'hsl(188, 82%, 35%)' : 'transparent',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            transition: 'background-color 200ms ease, border-color 200ms ease',
          }}
        >
          {isSelected && <Check size={14} color="white" />}
        </div>
      </button>
    );
  };

  return (
    <div
      data-testid="review-share-modal"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 100,
        display: 'flex',
        flexDirection: 'column',
        background: 'rgba(0, 0, 0, 0.6)',
        backdropFilter: 'blur(8px)',
      }}
    >
      {/* Background photo preview */}
      {photoUrl && (
        <div
          style={{
            position: 'absolute',
            inset: 0,
            zIndex: 0,
          }}
        >
          <img
            src={photoUrl}
            alt="Captured"
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              opacity: 0.2,
            }}
          />
        </div>
      )}

      {/* Content */}
      <div
        style={{
          position: 'relative',
          zIndex: 1,
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'flex-end',
          maxHeight: '100vh',
        }}
      >
        {/* Bottom sheet card */}
        <div
          style={{
            background: 'white',
            borderRadius: '1.5rem 1.5rem 0 0',
            maxHeight: '75vh',
            display: 'flex',
            flexDirection: 'column',
            boxShadow: '0 -4px 30px rgba(0, 0, 0, 0.15)',
          }}
        >
          {/* Drag handle */}
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              paddingTop: '0.75rem',
            }}
          >
            <div
              style={{
                width: '40px',
                height: '4px',
                borderRadius: '2px',
                background: 'hsl(210, 12%, 82%)',
              }}
            />
          </div>

          {/* Header */}
          <div style={{ padding: '1rem 1.25rem 0.75rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.25rem' }}>
              <Shield size={20} color="hsl(188, 82%, 35%)" />
              <h2
                style={{
                  fontSize: '1.125rem',
                  fontWeight: 700,
                  color: 'hsl(224, 71%, 4%)',
                  fontFamily: "'Space Grotesk', sans-serif",
                  margin: 0,
                }}
              >
                Review Sharing
              </h2>
            </div>
            <p
              style={{
                fontSize: '0.8rem',
                color: 'hsl(215, 16%, 35%)',
                margin: 0,
                lineHeight: 1.5,
              }}
            >
              {totalMatches === 0
                ? 'No recognized faces detected. Photo will be saved as private.'
                : `${totalMatches} ${totalMatches === 1 ? 'person' : 'people'} detected. Choose who to share with.`}
            </p>
          </div>

          {/* Scrollable face list */}
          <div
            style={{
              flex: 1,
              overflowY: 'auto',
              padding: '0 1.25rem',
            }}
          >
            {totalMatches === 0 ? (
              <div
                data-testid="no-matches-message"
                style={{
                  textAlign: 'center',
                  padding: '2rem 1rem',
                  color: 'hsl(215, 16%, 35%)',
                }}
              >
                <Users size={40} style={{ opacity: 0.3, marginBottom: '0.75rem' }} />
                <p style={{ fontSize: '0.875rem' }}>No recognized faces found</p>
                <p style={{ fontSize: '0.75rem', opacity: 0.7 }}>
                  The photo will be saved to your profile only.
                </p>
              </div>
            ) : (
              <>
                {/* Primary faces */}
                {primaryFaces.length > 0 && (
                  <div style={{ marginBottom: '0.75rem' }}>
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.375rem',
                        marginBottom: '0.5rem',
                      }}
                    >
                      <span
                        style={{
                          fontSize: '0.7rem',
                          fontWeight: 600,
                          color: 'hsl(215, 16%, 35%)',
                          textTransform: 'uppercase',
                          letterSpacing: '0.05em',
                        }}
                      >
                        Primary ({primaryFaces.length})
                      </span>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                      {primaryFaces.map((face) => (
                        <FaceItem key={face.recipient_id} face={face} />
                      ))}
                    </div>
                  </div>
                )}

                {/* Background faces */}
                {hasBackground && (
                  <div style={{ marginBottom: '0.75rem' }}>
                    <button
                      onClick={() => setShowBackground(!showBackground)}
                      data-testid="toggle-background-faces"
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.375rem',
                        marginBottom: '0.5rem',
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        padding: '0.25rem 0',
                      }}
                    >
                      <span
                        style={{
                          fontSize: '0.7rem',
                          fontWeight: 600,
                          color: 'hsl(215, 16%, 35%)',
                          textTransform: 'uppercase',
                          letterSpacing: '0.05em',
                        }}
                      >
                        Background ({backgroundFaces.length})
                      </span>
                      {showBackground ? (
                        <ChevronUp size={14} color="hsl(215, 16%, 35%)" />
                      ) : (
                        <ChevronDown size={14} color="hsl(215, 16%, 35%)" />
                      )}
                    </button>
                    {showBackground && (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                        {backgroundFaces.map((face) => (
                          <FaceItem key={face.recipient_id} face={face} />
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </>
            )}
          </div>

          {/* Actions */}
          <div
            style={{
              padding: '1rem 1.25rem',
              paddingBottom: 'max(1rem, env(safe-area-inset-bottom))',
              borderTop: '1px solid hsl(210, 12%, 92%)',
              display: 'flex',
              flexDirection: 'column',
              gap: '0.5rem',
            }}
          >
            {totalMatches > 0 ? (
              <>
                <button
                  onClick={handleConfirm}
                  disabled={confirming || selectedCount === 0}
                  data-testid="confirm-share-button"
                  style={{
                    width: '100%',
                    padding: '0.875rem',
                    borderRadius: '0.75rem',
                    border: 'none',
                    background: selectedCount > 0 ? 'hsl(188, 82%, 35%)' : 'hsl(210, 12%, 92%)',
                    color: selectedCount > 0 ? 'white' : 'hsl(215, 16%, 35%)',
                    fontSize: '0.9rem',
                    fontWeight: 600,
                    fontFamily: "'Space Grotesk', sans-serif",
                    cursor: selectedCount > 0 ? 'pointer' : 'not-allowed',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '0.5rem',
                    transition: 'background-color 200ms ease, transform 200ms ease',
                  }}
                >
                  {confirming ? (
                    <div className="spinner" style={{ width: '18px', height: '18px' }} />
                  ) : (
                    <>
                      <Check size={18} />
                      Share with {selectedCount} {selectedCount === 1 ? 'person' : 'people'}
                    </>
                  )}
                </button>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <button
                    onClick={onCancel}
                    data-testid="keep-private-button"
                    style={{
                      flex: 1,
                      padding: '0.75rem',
                      borderRadius: '0.75rem',
                      border: '1px solid hsl(210, 12%, 92%)',
                      background: 'transparent',
                      color: 'hsl(215, 16%, 35%)',
                      fontSize: '0.8rem',
                      fontWeight: 500,
                      cursor: 'pointer',
                      transition: 'background-color 200ms ease',
                    }}
                  >
                    Keep Private
                  </button>
                  <button
                    onClick={onSkip}
                    data-testid="skip-review-button"
                    style={{
                      flex: 1,
                      padding: '0.75rem',
                      borderRadius: '0.75rem',
                      border: '1px solid hsl(210, 12%, 92%)',
                      background: 'transparent',
                      color: 'hsl(215, 16%, 35%)',
                      fontSize: '0.8rem',
                      fontWeight: 500,
                      cursor: 'pointer',
                      transition: 'background-color 200ms ease',
                    }}
                  >
                    Auto (Primary only)
                  </button>
                </div>
              </>
            ) : (
              <button
                onClick={onCancel}
                data-testid="save-private-button"
                style={{
                  width: '100%',
                  padding: '0.875rem',
                  borderRadius: '0.75rem',
                  border: 'none',
                  background: 'hsl(188, 82%, 35%)',
                  color: 'white',
                  fontSize: '0.9rem',
                  fontWeight: 600,
                  fontFamily: "'Space Grotesk', sans-serif",
                  cursor: 'pointer',
                  transition: 'background-color 200ms ease',
                }}
              >
                Save to My Photos
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default ReviewShareModal;
